import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Rocket, Shield, Play, IndianRupee } from "lucide-react";
import { StayCommandHero, PublicStays, EconomicalBanner, PricingStrip } from "../components/homestay/StayCommandHero";

// User-uploaded marketing assets (HomeStay Feel)
const HERO_BANNER = "https://customer-assets-wrfwihn1.emergentagent.net/job_assist-on-demand/artifacts/xdwmc8ns_file_0000000038dc8211b8213a38ab815fca.png";
const BEDS_BANNER = "https://customer-assets-wrfwihn1.emergentagent.net/job_assist-on-demand/artifacts/wpy5j5pd_file_000000009d48821187a5902529078cac.png";
const ECONOMICAL = "https://customer-assets-wrfwihn1.emergentagent.net/job_assist-on-demand/artifacts/mxblnjft_file_0000000081a88211a509396e30cd206b.png";
const MASCOT_SIGN = "https://customer-assets-wrfwihn1.emergentagent.net/job_assist-on-demand/artifacts/c81jmv3z_HeylpMate_Sticker_07.png";

const PROPERTY_TYPES = [
  { k: "hotel", n: "Hotel", emoji: "🏨" },
  { k: "pg", n: "PG / Guest House", emoji: "🛏" },
  { k: "dormitory", n: "Dormitory", emoji: "🛌" },
  { k: "homestay", n: "Homestay", emoji: "🏡" },
  { k: "farmstay", n: "Farm Stay", emoji: "🌾" },
  { k: "holiday", n: "Holiday Home", emoji: "🏖" },
];

export default function HomeStay() {
  const [stayType, setStayType] = useState("");
  return (
    <div className="pb-20" data-testid="homestay-landing">
      <EconomicalBanner />
      <PricingStrip />

      <StayCommandHero />

      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
        <img src={HERO_BANNER} alt="HeylpMate HomeStay — Stay Local. Feel Home." className="w-full rounded-3xl border shadow-xl" style={{ borderColor: "#E4E7E5" }} />
      </section>

      {/* Property-type chips */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-6" data-testid="homestay-types">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {PROPERTY_TYPES.map((t) => (
            <button key={t.k} onClick={() => setStayType(stayType === t.k ? "" : t.k)}
                  data-testid={`stay-${t.k}`}
                  className="shrink-0 rounded-full border-2 px-4 py-2 text-sm font-bold hover:bg-emerald-50 transition-colors"
                  style={{ borderColor: stayType === t.k ? "#00604A" : "#00604A33", color: stayType === t.k ? "#fff" : "#00604A", background: stayType === t.k ? "#00604A" : "transparent" }}>
              <span className="mr-1">{t.emoji}</span>{t.n}
            </button>
          ))}
        </div>
      </section>

      <PublicStays type={stayType} />

      {/* Demo CTA strip — "₹37 per day" */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-6" data-testid="homestay-demo-strip">
        <div className="rounded-3xl p-5 md:p-6 flex items-center justify-between gap-4 flex-wrap"
             style={{ background: "linear-gradient(135deg,#FFF3EA 0%,#FFE8D5 60%,#FFDDC0 100%)",
                      border: "2px solid #F4511844" }}>
          <div className="flex items-center gap-3">
            <img src={MASCOT_SIGN} alt="" className="w-14 h-14 rounded-full border-2" style={{ borderColor: "#F45118" }} />
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "#F45118" }}>Try before you subscribe</div>
              <div className="font-display text-xl md:text-2xl font-bold" style={{ color: "#00604A" }}>
                Run your entire property for <span style={{ color: "#F45118" }}>₹37 a day</span>
              </div>
              <div className="text-xs" style={{ color: "#667085" }}>₹999/month Featured plan · less than ₹34 a day. Live demo loaded with real data.</div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/homestay/owner" data-testid="homestay-owner-btn"
                  className="rounded-full px-5 py-2.5 font-bold text-sm shadow-lg inline-flex items-center gap-2 bg-white border-2"
                  style={{ borderColor: "#00604A", color: "#00604A" }}>
              <Shield className="w-4 h-4" /> Owner Login
            </Link>
            <Link to="/homestay/demo" data-testid="homestay-demo-btn"
                  className="rounded-full px-5 py-2.5 font-bold text-white text-sm shadow-lg inline-flex items-center gap-2"
                  style={{ background: "#00604A" }}>
              <Play className="w-4 h-4" /> Try Demo
            </Link>
            <Link to="/pay-upi?amount=999&purpose=homestay_featured" data-testid="homestay-upgrade-btn"
                  className="rounded-full px-5 py-2.5 font-bold text-white text-sm shadow-lg inline-flex items-center gap-2"
                  style={{ background: "#F45118" }}>
              <Rocket className="w-4 h-4" /> Upgrade — ₹999/mo
            </Link>
          </div>
        </div>
      </section>

      {/* Plan comparison */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-10" data-testid="homestay-plans">
        <div className="text-center">
          <span className="label-eyebrow">Pick your plan</span>
          <h2 className="mt-2 font-display text-4xl md:text-5xl font-bold" style={{ color: "#00604A" }}>
            Start free. <span className="italic" style={{ color: "#F45118" }}>Grow when ready.</span>
          </h2>
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-4 md:gap-5">
          <PlanCard
            testid="plan-free"
            title="FREE OWNER DASHBOARD" price="₹0" per="/month"
            tone="green"
            points={[
              "Basic property listing in HomeStay search",
              "Standard placement in search results",
              "Bookings mediated by HeylpMate Admin",
              "Property profile & photos",
              "Booking enquiries & leads",
              "Customer reviews",
              "Free Human Assistance — explore once",
            ]}
            footer="HeylpMate takes ₹25 platform fee per confirmed booking."
            cta={{ to: "/signup-quick?role=assistant&intent=homestay_free", label: "List for Free →" }}
          />
          <PlanCard
            testid="plan-featured" featured
            title="FEATURED OWNER" price="₹999" per="/month"
            tone="orange"
            subtitle="Complete HeylpMate Stay Command Centre"
            points={[
              "Direct customer booking + instant mobile alerts",
              "Live 2D + Isometric 3D bed board (up to 50 beds)",
              "Manager login (1 free · additional per Admin)",
              "Inventory photo tracking + mismatch alerts",
              "Revenue & expense management · monthly target",
              "Repair, maintenance & vendor tickets",
              "Water & locker daily tracking",
              "Customer master data + stay history",
              "Daily Manager Summary (WhatsApp + in-app)",
              "FEATURED & VERIFIED badges · priority placement",
            ]}
            footer="Still ₹25 platform fee per confirmed booking. Pricing admin-editable."
            cta={{ to: "/homestay/owner", label: "Start 7-day free trial →" }}
          />
        </div>
        <div className="mt-6 rounded-3xl p-5 text-center text-sm"
             style={{ background: "#F7FBF9", border: "1px dashed #00604A55", color: "#00604A" }}>
          <b>Manage your entire property from your phone for less than ₹34 a day.</b>
        </div>
      </section>

      {/* Marketing banner 1 — beds */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-10">
        <img src={BEDS_BANNER} alt="HeylpMate Live Bed Status"
             className="w-full rounded-3xl border shadow-lg" style={{ borderColor: "#E4E7E5" }} />
      </section>

      {/* Marketing banner 2 — economical */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
        <img src={ECONOMICAL} alt="Economical Hostel / Dormitory / Homestay"
             className="w-full rounded-3xl border shadow-lg" style={{ borderColor: "#E4E7E5" }} />
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-12 text-center">
        <div className="rounded-3xl p-8 md:p-10"
             style={{ background: "linear-gradient(135deg,#00604A,#0B7A5A 60%,#F45118 130%)" }}>
          <h3 className="font-display text-3xl md:text-4xl font-bold text-white">
            See it before you subscribe.
          </h3>
          <p className="mt-2 text-white/90 text-sm">Try the live demo loaded with real property data. Resets every 24 hours.</p>
          <div className="mt-5 flex justify-center gap-3 flex-wrap">
            <Link to="/homestay/demo" className="rounded-full px-6 py-3 font-bold shadow-lg inline-flex items-center gap-2 bg-white"
                  style={{ color: "#00604A" }}>
              <Play className="w-4 h-4" /> Try Free Demo
            </Link>
            <Link to="/pay-upi?amount=999&purpose=homestay_featured"
                  className="rounded-full px-6 py-3 font-bold shadow-lg inline-flex items-center gap-2 text-white"
                  style={{ background: "#F45118" }}>
              <Rocket className="w-4 h-4" /> Go Featured · ₹999/mo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function PlanCard({ title, price, per, points, footer, cta, tone, featured, subtitle, testid }) {
  const bg = featured
    ? "linear-gradient(180deg,#FFF3EA 0%,#FFE8D5 100%)"
    : "linear-gradient(180deg,#E8F7EF 0%,#D5F0DF 100%)";
  const border = featured ? "#F45118" : "#00604A";
  return (
    <div className="rounded-3xl p-5 md:p-6 border-2 card-lift relative overflow-hidden"
         style={{ borderColor: border, background: bg }} data-testid={testid}>
      {featured && (
        <div className="absolute top-3 right-3 rounded-full px-3 py-1 text-[10px] font-bold text-white shadow"
             style={{ background: "#F45118" }}>⭐ RECOMMENDED</div>
      )}
      <div className="text-xs uppercase tracking-widest font-bold"
           style={{ color: featured ? "#F45118" : "#00604A" }}>{title}</div>
      {subtitle && <div className="text-sm font-bold mt-0.5" style={{ color: "#00604A" }}>{subtitle}</div>}
      <div className="mt-3 flex items-baseline gap-1">
        <IndianRupee className="w-6 h-6" style={{ color: featured ? "#F45118" : "#00604A" }} />
        <span className="font-display text-5xl font-bold"
              style={{ color: featured ? "#F45118" : "#00604A" }}>{price.replace("₹", "")}</span>
        <span className="text-sm font-bold" style={{ color: "#667085" }}>{per}</span>
      </div>
      <ul className="mt-4 space-y-1.5 text-sm">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"
                          style={{ color: featured ? "#F45118" : "#00604A" }} />
            <span style={{ color: "#334155" }}>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 text-[11px] italic" style={{ color: "#667085" }}>{footer}</div>
      <Link to={cta.to}
            className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-bold text-sm shadow-lg hover:opacity-95"
            style={{ background: featured ? "#F45118" : "#00604A" }}>
        {cta.label}
      </Link>
    </div>
  );
}
